import React, { useState, useMemo } from 'react';
import {
  Box,
  Flex,
  Input,
  Select,
  Button,
  ButtonGroup,
  Text,
  Badge,
  HStack,
  VStack,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Checkbox,
  CheckboxGroup,
  Stack,
  InputGroup,
  InputLeftElement,
  Icon,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  IconButton,
  SimpleGrid
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { Filter } from 'lucide-react';

interface CryptoAsset {
  id: string;
  symbol: string;
  name: string;
  alphaScore: number;
  confidence: string;
  marketCap: number;
  price: number;
  volume24h: number;
  priceChange24h: number;
  stage: string;
  category: string;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  discovered: string;
  description: string;
  socialScore: number;
  devActivity: number;
  communitySize: number;
  alerts: string[];
  sector: string;
  change24h: number;
}

interface FilterState {
  search: string;
  riskLevels: string[];
  sectors: string[];
  priceRange: [number, number];
  marketCapRange: [number, number];
  alphaScoreRange: [number, number];
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

interface AdvancedFilterProps {
  assets: CryptoAsset[];
  onFilteredAssets: (assets: CryptoAsset[]) => void;
  onFiltersChange?: (filters: FilterState) => void;
}

const AdvancedFilter: React.FC<AdvancedFilterProps> = ({
  assets,
  onFilteredAssets,
  onFiltersChange
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    riskLevels: [],
    sectors: [],
    priceRange: [0, 100000],
    marketCapRange: [0, 1000000000],
    alphaScoreRange: [0, 100],
    sortBy: 'marketCap',
    sortOrder: 'desc'
  });

  // Extract unique sectors from assets
  const availableSectors = useMemo(() => {
    const sectors = assets.map(asset => asset.sector).filter(Boolean);
    return [...new Set(sectors)];
  }, [assets]);

  // Calculate price and market cap ranges
  const priceStats = useMemo(() => {
    const prices = assets.map(asset => asset.price);
    return {
      min: Math.floor(Math.min(...prices)),
      max: Math.ceil(Math.max(...prices))
    };
  }, [assets]);

  const marketCapStats = useMemo(() => {
    const marketCaps = assets.map(asset => asset.marketCap);
    return {
      min: Math.floor(Math.min(...marketCaps)),
      max: Math.ceil(Math.max(...marketCaps))
    };
  }, [assets]);

  // Filter and sort assets
  const filteredAssets = useMemo(() => {
    let filtered = assets.filter(asset => {
      // Search filter
      const searchMatch = !filters.search || 
        asset.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        asset.symbol.toLowerCase().includes(filters.search.toLowerCase());

      // Risk level filter
      const riskMatch = filters.riskLevels.length === 0 || 
        filters.riskLevels.includes(asset.riskLevel);

      // Sector filter
      const sectorMatch = filters.sectors.length === 0 || 
        filters.sectors.includes(asset.sector);

      // Price range filter
      const priceMatch = asset.price >= filters.priceRange[0] && 
        asset.price <= filters.priceRange[1];

      // Market cap range filter
      const marketCapMatch = asset.marketCap >= filters.marketCapRange[0] && 
        asset.marketCap <= filters.marketCapRange[1];

      // Alpha score range filter
      const alphaScoreMatch = asset.alphaScore >= filters.alphaScoreRange[0] && 
        asset.alphaScore <= filters.alphaScoreRange[1];

      return searchMatch && riskMatch && sectorMatch && priceMatch && 
             marketCapMatch && alphaScoreMatch;
    });

    // Sort filtered assets
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      switch (filters.sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'price':
          aValue = a.price;
          bValue = b.price;
          break;
        case 'change24h':
          aValue = a.change24h;
          bValue = b.change24h;
          break;
        case 'marketCap':
          aValue = a.marketCap;
          bValue = b.marketCap;
          break;
        case 'volume24h':
          aValue = a.volume24h;
          bValue = b.volume24h;
          break;
        case 'alphaScore':
          aValue = a.alphaScore;
          bValue = b.alphaScore;
          break;
        default:
          aValue = a.marketCap;
          bValue = b.marketCap;
      }

      if (filters.sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [assets, filters]);

  // Update parent component when filters change
  React.useEffect(() => {
    onFilteredAssets(filteredAssets);
    onFiltersChange?.(filters);
  }, [filteredAssets, filters, onFilteredAssets, onFiltersChange]);

  const updateFilter = (key: keyof FilterState, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      riskLevels: [],
      sectors: [],
      priceRange: [priceStats.min, priceStats.max],
      marketCapRange: [marketCapStats.min, marketCapStats.max],
      alphaScoreRange: [0, 100],
      sortBy: 'marketCap',
      sortOrder: 'desc'
    });
  };

  const formatCurrency = (value: number) => {
    if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
    if (value >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
    return `$${value.toFixed(2)}`;
  };

  return (
    <Box>
      {/* Quick Filters Bar */}
      <Flex
        gap={4}
        mb={6}
        p={4}
        bg="white"
        borderRadius="lg"
        shadow="sm"
        border="1px solid"
        borderColor="gray.200"
        wrap="wrap"
        alignItems="center"
      >
        <InputGroup maxW="300px">
          <InputLeftElement pointerEvents="none">
            <Icon as={SearchIcon} color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search assets..."
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
          />
        </InputGroup>

        <ButtonGroup size="sm" isAttached variant="outline">
          {['LOW', 'MEDIUM', 'HIGH'].map((risk) => (
            <Button
              key={risk}
              onClick={() => {
                const newRiskLevels = filters.riskLevels.includes(risk)
                  ? filters.riskLevels.filter(r => r !== risk)
                  : [...filters.riskLevels, risk];
                updateFilter('riskLevels', newRiskLevels);
              }}
              colorScheme={filters.riskLevels.includes(risk) ? 'blue' : 'gray'}
              variant={filters.riskLevels.includes(risk) ? 'solid' : 'outline'}
            >
              {risk}
            </Button>
          ))}
        </ButtonGroup>

        <Select
          value={filters.sortBy}
          onChange={(e) => updateFilter('sortBy', e.target.value)}
          w="200px"
          size="sm"
        >
          <option value="marketCap">Market Cap</option>
          <option value="price">Price</option>
          <option value="change24h">24h Change</option>
          <option value="volume24h">Volume</option>
          <option value="alphaScore">Alpha Score</option>
          <option value="name">Name</option>
        </Select>

        <ButtonGroup size="sm" isAttached>
          <Button
            onClick={() => updateFilter('sortOrder', 'desc')}
            colorScheme={filters.sortOrder === 'desc' ? 'blue' : 'gray'}
            variant={filters.sortOrder === 'desc' ? 'solid' : 'outline'}
          >
            ↓
          </Button>
          <Button
            onClick={() => updateFilter('sortOrder', 'asc')}
            colorScheme={filters.sortOrder === 'asc' ? 'blue' : 'gray'}
            variant={filters.sortOrder === 'asc' ? 'solid' : 'outline'}
          >
            ↑
          </Button>
        </ButtonGroup>

        <IconButton
          aria-label="Advanced Filters"
          icon={<Filter size={16} />}
          onClick={onOpen}
          size="sm"
          colorScheme="blue"
          variant="outline"
        />

        <Button size="sm" variant="ghost" onClick={clearFilters}>
          Clear All
        </Button>

        <Text fontSize="sm" color="gray.600" ml="auto">
          {filteredAssets.length} of {assets.length} assets
        </Text>
      </Flex>

      {/* Advanced Filters Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Advanced Filters</DrawerHeader>
          <DrawerBody>
            <VStack spacing={8} align="stretch">
              {/* Sectors */}
              <Box>
                <Text fontWeight="bold" mb={3}>Sectors</Text>
                <CheckboxGroup
                  value={filters.sectors}
                  onChange={(values) => updateFilter('sectors', values)}
                >
                  <SimpleGrid columns={2} spacing={2}>
                    {availableSectors.map((sector) => (
                      <Checkbox key={sector} value={sector}>
                        {sector}
                      </Checkbox>
                    ))}
                  </SimpleGrid>
                </CheckboxGroup>
              </Box>

              {/* Price Range */}
              <Box>
                <Text fontWeight="bold" mb={3}>
                  Price Range: {formatCurrency(filters.priceRange[0])} - {formatCurrency(filters.priceRange[1])}
                </Text>
                <Slider
                  min={priceStats.min}
                  max={priceStats.max}
                  value={filters.priceRange[1]}
                  onChange={(value) => updateFilter('priceRange', [filters.priceRange[0], value])}
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb />
                </Slider>
              </Box>

              {/* Market Cap Range */}
              <Box>
                <Text fontWeight="bold" mb={3}>
                  Market Cap: {formatCurrency(filters.marketCapRange[0])} - {formatCurrency(filters.marketCapRange[1])}
                </Text>
                <Slider
                  min={marketCapStats.min}
                  max={marketCapStats.max}
                  value={filters.marketCapRange[1]}
                  onChange={(value) => updateFilter('marketCapRange', [filters.marketCapRange[0], value])}
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb />
                </Slider>
              </Box>

              {/* Alpha Score Range */}
              <Box>
                <Text fontWeight="bold" mb={3}>
                  Alpha Score: {filters.alphaScoreRange[0]}% - {filters.alphaScoreRange[1]}%
                </Text>
                <Slider
                  min={0}
                  max={100}
                  value={filters.alphaScoreRange[1]}
                  onChange={(value) => updateFilter('alphaScoreRange', [filters.alphaScoreRange[0], value])}
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb />
                </Slider>
              </Box>

              {/* Active Filters Summary */}
              <Box>
                <Text fontWeight="bold" mb={3}>Active Filters</Text>
                <Flex wrap="wrap" gap={2}>
                  {filters.search && (
                    <Badge colorScheme="blue">Search: {filters.search}</Badge>
                  )}
                  {filters.riskLevels.map(risk => (
                    <Badge key={risk} colorScheme="red">{risk} Risk</Badge>
                  ))}
                  {filters.sectors.map(sector => (
                    <Badge key={sector} colorScheme="green">{sector}</Badge>
                  ))}
                  {(filters.priceRange[1] < priceStats.max || filters.priceRange[0] > priceStats.min) && (
                    <Badge colorScheme="purple">Price Range</Badge>
                  )}
                  {(filters.marketCapRange[1] < marketCapStats.max || filters.marketCapRange[0] > marketCapStats.min) && (
                    <Badge colorScheme="orange">Market Cap Range</Badge>
                  )}
                  {(filters.alphaScoreRange[1] < 100 || filters.alphaScoreRange[0] > 0) && (
                    <Badge colorScheme="teal">Alpha Score Range</Badge>
                  )}
                </Flex>
              </Box>

              <Button colorScheme="red" variant="outline" onClick={clearFilters}>
                Clear All Filters
              </Button>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default AdvancedFilter;